import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ScanLine, Search, CheckCircle2, AlertTriangle, Package, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader, Panel, BrandButton, StatCard } from "@/components/PageHeader";
import { useStatsEvents } from "@/components/EventPicker";
import { GateUndoActions } from "@/components/GateUndo";
import { useApp } from "@/context/AppContext";
import { api, cedi, errMsg, fmtDate, initials, showPin } from "@/lib/api";

const ScanBox = ({ onCode, busy }) => {
  const ref = useRef(null);
  const [val, setVal] = useState("");
  const [focused, setFocused] = useState(false);
  const timer = useRef(null);
  useEffect(() => { if (!busy) { ref.current?.focus(); } }, [busy]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const submit = (v) => { clearTimeout(timer.current); if (v.trim()) { onCode(v); } setVal(""); };
  const onChange = (e) => {
    const v = e.target.value;
    setVal(v);
    clearTimeout(timer.current);
    if (/X-GAMLS-TICKET:G\d{6,}/i.test(v)) { timer.current = setTimeout(() => submit(v), 700); }
  };
  const onKey = (e) => {
    if (e.key !== "Enter") { return; }
    e.preventDefault();
    const up = val.toUpperCase();
    if (up.includes("BEGIN:VCARD") && !up.includes("END:VCARD")) { setVal(val + " "); return; }
    submit(val);
  };
  return (
    <div className={`relative rounded-lg bg-[#111] p-6 sm:p-8 text-white ${focused ? "ring-2 ring-[#E60000]" : ""}`} data-testid="checkin-scanner" onClick={() => ref.current?.focus()}>
      <div className="flex items-center gap-3">
        <ScanLine className={`w-8 h-8 ${focused ? "text-[#E60000]" : "text-white/40"}`} />
        <div>
          <div className="font-heading font-bold text-lg" data-testid="checkin-scanner-status">{busy ? "Checking…" : focused ? "Scanner ready" : "Click here, then scan"}</div>
          <div className="text-xs text-white/50">Scan a badge QR code with the scanner. You can also type a ticket number and press Enter.</div>
        </div>
      </div>
      <Input ref={ref} value={val} onChange={onChange} onKeyDown={onKey} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        placeholder="Waiting for scan…" aria-label="Scanner input" autoComplete="off" className="mt-5 font-mono h-11 bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="gate-scan-input" />
    </div>
  );
};

const PaymentLine = ({ p }) => {
  if (!p) return <div className="mt-3 text-xs text-gray-600" data-testid="checkin-payment-none">No registration payment record found.</div>;
  const tone = p.status === "Paid" ? "text-emerald-700" : p.status === "Free" ? "text-yellow-700" : p.status === "Part Paid" ? "text-amber-700" : "text-[#B30000]";
  return (
    <div className="mt-3 rounded-md bg-white/70 border px-3 py-2 text-xs flex flex-wrap gap-x-4 gap-y-1" data-testid="checkin-payment">
      <span>{p.category_name}</span>
      <span className={`font-semibold ${tone}`} data-testid="checkin-payment-status">{p.status}</span>
      {p.balance > 0 && <span className="font-mono">Balance {cedi(p.balance)}</span>}
      {p.exempt && <span className="text-emerald-700 font-semibold">Souvenir without payment</span>}
    </div>
  );
};

const Result = ({ res, onKit, onUndo }) => {
  const d = res.delegate;
  if (res.not_paid) {
    return (
      <div className="rounded-lg border-2 border-[#B30000] bg-red-50 p-5 fade-up" data-testid="checkin-result-card">
        <div className="flex items-center gap-2 text-sm font-bold text-[#B30000]" data-testid="checkin-not-paid"><AlertTriangle className="w-5 h-5" />Not paid — entry refused</div>
        <div className="font-heading font-bold text-lg mt-3" data-testid="checkin-result-name">{d.name}</div>
        {d.ticket_no && <div className="font-mono text-sm font-extrabold text-[#E60000]" data-testid="checkin-result-ticket">Ticket {d.ticket_no}</div>}
        <PaymentLine p={d.payment} />
        <p className="mt-3 text-sm text-[#7A0000]" data-testid="checkin-not-paid-advice">Please ask the participant to complete payment at the accounts desk, then scan again to check in.</p>
      </div>
    );
  }
  return (
    <div className={`rounded-lg border-2 p-5 fade-up ${res.undone ? "border-gray-300 bg-white" : res.already_checked_in ? "border-amber-400 bg-amber-50" : "border-emerald-500 bg-emerald-50"}`} data-testid="checkin-result-card">
      <div className="flex items-center gap-2 text-sm font-bold">
        {res.undone ? <><AlertTriangle className="w-5 h-5 text-amber-600" />{d.checked_in ? "Kit issue undone" : "Check-in undone"}</> : res.lookup ? <><CheckCircle2 className="w-5 h-5 text-emerald-600" />Participant found</> : res.already_checked_in ? <><AlertTriangle className="w-5 h-5 text-amber-600" />Already checked in</> : <><CheckCircle2 className="w-5 h-5 text-emerald-600" />Access granted</>}
      </div>
      <div className="flex items-center gap-4 mt-4">
        <div className="w-16 h-16 rounded-md bg-[#111] text-white grid place-items-center font-heading text-xl font-bold">{initials(d.name)}</div>
        <div>
          <div className="font-heading font-bold text-lg" data-testid="checkin-result-name">{d.name}</div>
          {d.ticket_no && <div className="font-mono text-sm font-extrabold text-[#E60000]" data-testid="checkin-result-ticket">Ticket {d.ticket_no}</div>}
          <div className="font-mono text-xs">{showPin(d.pin)}</div>
          <div className="text-xs text-gray-600">{d.facility} · {d.region}</div>
        </div>
      </div>
      <PaymentLine p={d.payment} />
      <div className="mt-4 flex flex-wrap gap-2 items-center justify-between">
        <span className="text-xs text-gray-600">{d.checked_in ? `Checked in ${fmtDate(d.checked_in_at)}` : "Not checked in yet"}</span>
        {d.kit_issued ? <span className="text-xs font-semibold text-emerald-700" data-testid="checkin-kit-issued">Kit issued</span> : !d.checked_in ?
          <span className="text-xs font-semibold text-amber-700" data-testid="checkin-kit-needs-checkin">Check in at the gate first</span> : d.payment?.souvenir_ok ?
          <BrandButton variant="dark" onClick={onKit} data-testid="checkin-issue-kit-button"><Package className="w-4 h-4" />Issue badge & kit</BrandButton> :
          <span className="text-xs font-semibold text-[#B30000]" data-testid="checkin-kit-blocked">Souvenir after full payment</span>}
      </div>
      <GateUndoActions txnId={d.txn_id} checkedIn={d.checked_in} kitIssued={d.kit_issued} onDone={onUndo} prefix="checkin" />
    </div>
  );
};

const EventChooser = ({ events, onPick }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="checkin-event-list">
    {events.map((e) => (
      <button key={e.id} onClick={() => onPick(e.id)} className="text-left rounded-lg border border-[#E5E7EB] bg-white p-5 hover:border-[#E60000] hover:shadow-sm transition-[border-color,box-shadow]" data-testid={`checkin-event-${e.id}`}>
        <div className="font-heading font-bold text-lg">{e.title}</div>
        <div className="text-xs text-gray-500 mt-1">{e.date || e.start_date || "Date not set"}</div>
        <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#E60000]"><ScanLine className="w-4 h-4" />Open scan window</div>
      </button>
    ))}
  </div>
);

export default function CheckIn() {
  const { refresh, role } = useApp();
  const warden = role === "Souvenir Warden";
  const [events] = useStatsEvents();
  const [eventId, setEventId] = useState("");
  const current = (events || []).find((e) => e.id === eventId);
  const [stats, setStats] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const loadStats = useCallback(() => {
    if (eventId) { api.get("/gate/stats", { params: { event_id: eventId } }).then((r) => setStats(r.data)).catch(() => setStats(null)); }
  }, [eventId]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const verify = async (p) => {
    setBusy(true);
    try {
      if (warden) {
        const r = await api.post("/gate/resolve", { code: p, event_id: eventId });
        setRes({ delegate: r.data, already_checked_in: false, lookup: true });
      } else {
        const r = await api.post("/gate/checkin", { code: p, event_id: eventId });
        setRes(r.data);
        if (r.data.not_paid) { toast.error(`${r.data.delegate.name} has not paid. Payment is needed before check-in.`); }
        else { toast[r.data.already_checked_in ? "warning" : "success"](r.data.already_checked_in ? "Participant already checked in" : `Welcome, ${r.data.delegate.name}`); }
        loadStats();
        refresh();
      }
    } catch (e) {
      setRes(null);
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const kit = async () => {
    try {
      const r = await api.post("/gate/kit", { txn_id: res.delegate.txn_id });
      setRes({ ...res, undone: false, delegate: r.data });
      toast.success("Badge & kit issued. A confirmation SMS will be sent to the participant's phone");
      loadStats();
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pick = (id) => { setEventId(id); setRes(null); setStats(null); };
  const title = warden ? "Souvenir & Kit Collection" : "Onsite QR Check-In";
  if (!current) {
    return (
      <div data-testid="checkin-page">
        <PageHeader eyebrow="Check-In and Souvenir Collection" title={title} description="Choose the event you are checking participants into. The scan window then accepts that event's participants only." />
        {events === null ? <div className="text-gray-500" data-testid="checkin-events-loading">Loading events…</div> : events.some((e) => !e.archived) ? <EventChooser events={events.filter((e) => !e.archived)} onPick={pick} /> :
          <div className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-500" data-testid="checkin-no-events">No open events. Create an event (or unarchive one) to start checking participants in.</div>}
      </div>
    );
  }

  return (
    <div data-testid="checkin-page">
      <PageHeader eyebrow={`Scanning for · ${current.title}`} title={title} description={warden ? "Scan a participant badge QR or type their ticket number to issue their badge, kit and souvenirs." : "Scan participant badge QR codes or type a ticket number. Only participants registered for this event can check in, once each."}
        actions={<BrandButton variant="outline" onClick={() => pick("")} data-testid="checkin-change-event"><ArrowLeft className="w-4 h-4" />Change event</BrandButton>} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <StatCard label="Checked in" value={stats ? `${stats.checked_in} / ${stats.registered}` : "–"} testId="checkin-count" />
        <StatCard label="Kits issued" value={stats ? stats.kits : "–"} testId="checkin-kits-count" delay={60} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <div className="space-y-4">
          <ScanBox onCode={verify} busy={busy} />
          <Panel title="Manual lookup">
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); verify(pin); setPin(""); }}>
              <Input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Ticket no., AHPC PIN, email or phone" aria-label="Ticket number, AHPC PIN, email or phone" className="font-mono h-10" data-testid="gate-pin-input" />
              <BrandButton type="submit" variant="dark" data-testid="gate-verify-delegate-button"><Search className="w-4 h-4" />Verify</BrandButton>
            </form>
          </Panel>
        </div>
        <div className="space-y-4">
          {res ? <Result res={res} onKit={kit} onUndo={(data) => { setRes({ ...res, already_checked_in: false, undone: true, delegate: data }); loadStats(); }} /> : <div className="rounded-lg border-2 border-dashed border-gray-300 p-10 text-center text-sm text-gray-500" data-testid="checkin-awaiting">Awaiting scan…</div>}
          <Panel title="Recent check-ins" testId="checkin-recent-list">
            <ul className="divide-y -my-2">
              {(stats?.recent || []).map((d) => (
                <li key={d.id} className="py-2 flex justify-between text-sm" data-testid={`checkin-recent-${d.id}`}><span>{d.name}</span><span className="text-xs text-gray-500">{d.region}</span></li>
              ))}
              {stats && !stats.recent.length && <li className="py-2 text-sm text-gray-500" data-testid="checkin-recent-empty">No check-ins yet for this event</li>}
            </ul>
          </Panel>
        </div>
      </div>
    </div>
  );
}
